import * as React from 'react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Label } from '../Label';
import { Button } from '../Button';
import type { ChipInputProps } from './types';

// Split pasted or typed values on commas and line breaks.
const DEFAULT_SEPARATOR_PATTERN = /[,\n]+/;

function splitRawValue(rawValue: string, separatorPattern: RegExp) {
  return rawValue
    .split(separatorPattern)
    .map((part) => part.trim())
    .filter(Boolean);
}

function ChipLabel({ chip, className }: { chip: string; className?: string }) {
  return (
    <span
      onMouseDown={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      className={cn('eye:text-grey-140 eye:text-14!', className)}
    >
      {chip}
    </span>
  );
}

export const ChipInput = React.forwardRef<HTMLInputElement, ChipInputProps>(
  (
    {
      value,
      defaultValue = [],
      onValueChange,
      label,
      error = false,
      removeButtonLabel,
      addOnBlur = true,
      separatorPattern = DEFAULT_SEPARATOR_PATTERN,
      deduplicate = true,
      placeholder,
      chipClassName,
      chipLabelClassName,
      chipsContainerClassName,
      inputClassName,
      containerClassName,
      disabled,
      id,
      required,
      onBlur,
      onKeyDown,
      ...inputProps
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const [inputValue, setInputValue] = React.useState('');
    const rejectedInputValueRef = React.useRef<string | null>(null);
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const chips = value ?? internalValue;
    const isControlled = value !== undefined;
    const hasLabel = label != null;

    const updateValue = (nextValue: string[]) => {
      const shouldUpdate = onValueChange?.(nextValue);

      if (shouldUpdate === false) {
        return false;
      }

      if (!isControlled) {
        setInternalValue(nextValue);
      }

      return true;
    };

    const commitRawValue = (rawValue: string) => {
      const nextItems = splitRawValue(rawValue, separatorPattern);

      if (!nextItems.length) {
        setInputValue('');
        return;
      }

      const nextChips = [...chips];

      nextItems.forEach((item) => {
        if (!deduplicate || !nextChips.includes(item)) {
          nextChips.push(item);
        }
      });

      const didUpdate = updateValue(nextChips);

      if (didUpdate) {
        setInputValue('');
        rejectedInputValueRef.current = null;
        return;
      }

      rejectedInputValueRef.current = rawValue;
      setInputValue(rawValue);
    };

    const handleInputValueChange = (nextInputValue: string) => {
      if (rejectedInputValueRef.current && nextInputValue === '') {
        setInputValue(rejectedInputValueRef.current);
        rejectedInputValueRef.current = null;
        return;
      }

      rejectedInputValueRef.current = null;
      setInputValue(nextInputValue);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented || disabled) return;

      const shouldCommit =
        event.key === 'Enter' ||
        event.key === ',' ||
        (event.key === 'Tab' && inputValue.trim().length > 0);

      if (!shouldCommit) return;

      event.preventDefault();
      commitRawValue(event.key === ',' ? inputValue + event.key : inputValue);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      if (addOnBlur && inputValue.trim()) {
        commitRawValue(inputValue);
      }
      onBlur?.(event);
    };

    return (
      <ComboboxPrimitive.Root<string, true>
        multiple
        items={chips}
        value={chips}
        inputValue={inputValue}
        itemToStringLabel={(item) => item}
        itemToStringValue={(item) => item}
        onValueChange={(nextValue) => updateValue(nextValue ?? [])}
        onInputValueChange={handleInputValueChange}
      >
        <div className='eye:group/combobox-field eye:block eye:w-full'>
          <div
            className={cn(
              'eye:pb-6 eye:relative eye:flex eye:min-h-[44px] eye:w-full eye:items-start eye:rounded-md eye:border eye:bg-grey-5 eye:px-12 eye:py-8',
              'eye:focus-within:outline-2 eye:focus-within:outline-offset-3',
              disabled && 'eye:bg-grey-20',
              error
                ? 'eye:border-red-100 eye:hover:border-red-100'
                : 'hover:eye:border-black eye:border-grey-100',
              containerClassName,
            )}
          >
            {hasLabel ? (
              <Label
                htmlFor={inputId}
                className={cn(
                  'eye:body-s-highlight eye:absolute eye:top-0 eye:left-12 eye:z-10 eye:m-0 eye:max-w-[calc(100%-32px)] eye:-translate-y-1/2 eye:bg-label-gradient eye:px-[3px] eye:leading-none eye:whitespace-nowrap',
                  'eye:group-has-disabled/combobox-field:text-grey-disabled',
                  'eye:group-has-[[data-slot=chip-input-control][aria-invalid=true]]/combobox-field:text-red-100',
                )}
              >
                {label}
                {required ? <span aria-hidden='true'>*</span> : null}
              </Label>
            ) : null}
            <ComboboxPrimitive.Chips
              className={cn(
                'eye:flex eye:w-full eye:flex-wrap eye:items-center eye:gap-4xs',
                chipsContainerClassName,
                chips.length > 0 && 'eye:mt-4xs',
              )}
            >
              {chips.map((chip, index) => (
                <ComboboxPrimitive.Chip
                  key={`${chip}-${index}`}
                  data-slot='combobox-chip'
                  className={cn(
                    'eye:body-s eye:flex eye:min-h-24 eye:max-w-full eye:min-w-0 eye:items-center eye:gap-4 eye:rounded-md eye:border eye:border-grey-40 eye:bg-grey-20 eye:p-[2px] eye:px-12 eye:wrap-break-word eye:whitespace-normal',
                    chipClassName,
                  )}
                >
                  <ChipLabel chip={chip} className={chipLabelClassName} />
                  <ComboboxPrimitive.ChipRemove
                    aria-label={removeButtonLabel}
                    render={
                      <Button type='button' variant='ghost' size='icon-xs' />
                    }
                  >
                    <X size={14} aria-hidden />
                  </ComboboxPrimitive.ChipRemove>
                </ComboboxPrimitive.Chip>
              ))}

              <ComboboxPrimitive.Input
                {...inputProps}
                ref={ref}
                id={inputId}
                data-slot='chip-input-control'
                disabled={disabled}
                required={required && chips.length === 0}
                placeholder={placeholder}
                aria-invalid={error}
                className={cn(
                  'eye:body-m eye:min-h-32 eye:min-w-[8rem] eye:flex-1 eye:self-center eye:bg-transparent eye:outline-none eye:placeholder:text-grey-placeholder!',
                  disabled && 'eye:cursor-not-allowed eye:text-grey-disabled',
                  inputClassName,
                )}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
              />
            </ComboboxPrimitive.Chips>
          </div>
        </div>
      </ComboboxPrimitive.Root>
    );
  },
);

ChipInput.displayName = 'ChipInput';
